/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, memo, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { LiveChatContext } from '../../screens/LiveChatScreen';
import moment from 'moment';

// Component imports
import { Col, Divider, Row, Typography } from 'antd';
import InboxItem from './InboxItem';
import IncomingRequest from './IncomingRequest';
import LiveChatFilters from './LiveChatFilters';
import InfiniteScrollList from '../UI/InfiniteScrollList';

// Misc imports
import { markAsImportant, setInboxFilters } from '../../store/liveChat/liveChatThunks';
import { useFetchConversationsQuery, useFetchRequestsQuery } from '../../store/liveChat/liveChatApi';
import { isEmptyArray } from '../../utils/is/isEmptyArray';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { CONVERSATION_STATUSES, LIVECHAT_FLAGS, LIVECHAT_SCREEN_TABS, REQUEST_STATUSES } from '../../constants/liveChat';
import { usePrevious } from '../../utils/hooks/usePrevious';

const ConversationsList = () => {
  const { orgId } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const listRef = useRef();

  const [searchParams, setSearchParams] = useSearchParams();
  const [shouldLoadMoreConversations, setShouldLoadMoreConversations] = useState(false);
  // Helper flag to trigger conversation select useEffect when conversation query is not fetching new data (no status update)
  const [triggerEffectFlag, setTriggerEffectFlag] = useState(0);
  const previousTab = usePrevious(searchParams.get('tab'));

  const { currentUser } = useSelector((state) => state.auth);
  const { setInboxOpen, setSidebarOpen, isMobileSize, setIsEditingAutomations, setIsEditingResponses } = useContext(LiveChatContext);
  const { data: organization } = getCurrentQueryState('fetchOrganization', { orgId });
  const { inboxFilters } = useSelector((state) => state.livechat);
  const { data: conversations, isFetching: isFetchingConversations } = useFetchConversationsQuery({ orgId,
    urlParams: {
      skip: inboxFilters.skip,
      take: 20,
      status: inboxFilters.status,
      isTransferred: inboxFilters.transferredOnly,
      isUnread: inboxFilters.unreadOnly,
      orderByUnread: inboxFilters.orderByUnread,
      ...(inboxFilters?.isOwner !== undefined && { isOwner: inboxFilters.isOwner }),
      ...(!isEmptyArray(inboxFilters?.bots) && { tenantIds: inboxFilters.bots?.join(',') }),
      ...(!isEmptyArray(inboxFilters?.users) &&{ userIds: inboxFilters.users?.join(',') }),
      ...(inboxFilters?.date && { dateRange: `${encodeURIComponent(inboxFilters?.date === 'custom' ? inboxFilters.customDate[0] : inboxFilters?.date)},${encodeURIComponent(inboxFilters?.date === 'custom' ? inboxFilters.customDate[1] : moment.tz(new Date(), organization?.timezone).endOf('day').format())}` }),
      // Search fields
      ...(inboxFilters?.name && { name: inboxFilters.name }),
      ...(inboxFilters?.email && { email: inboxFilters.email }),
      ...(inboxFilters?.searchText && { searchText: inboxFilters.searchText }),
      ...(inboxFilters?.sessionId && { sessionId: inboxFilters.sessionId }),
      ...(inboxFilters?.conversationId && { conversationId: inboxFilters.conversationId })
    },
    replace: inboxFilters.skip === 0 || inboxFilters.replace
  }, { skip: !searchParams.get('tab') || !organization });
  // Access currently cached query args
  const { originalArgs: cachedQueryArgs } = getCurrentQueryState('fetchConversations', { orgId });

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(location.search);
    if (
      !isFetchingConversations
      && conversations?.total > 0
      && (!urlSearchParams.get('conversation') || isNaN(urlSearchParams.get('conversation')))
      && [LIVECHAT_SCREEN_TABS.OPEN, LIVECHAT_SCREEN_TABS.CLOSED].includes(urlSearchParams.get('tab'))
      //Compare cached and current status arg to fix selecting the wrong conversation when switching between open/closed
      && cachedQueryArgs?.urlParams?.status === inboxFilters.status
    ) {
      // If there is no conversationId in the URL, we set the first conversation as selected
      setSearchParams({
        tab: searchParams.get('tab'),
        conversation: conversations?.data[0]?.id
      });
    }
  }, [conversations, isFetchingConversations, triggerEffectFlag]);

  // We use the inboxFilters value for the first page fetch to prevent double requests
  useEffect(() => {
    if (inboxFilters.replace && listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [inboxFilters]);

  // Update state values for skip and status when switch tabs to properly update useFetchConversationsQuery params
  useEffect(() => {
    if ([LIVECHAT_SCREEN_TABS.OPEN, LIVECHAT_SCREEN_TABS.CLOSED].includes(searchParams.get('tab')) && searchParams.get('tab') !== previousTab) {
      dispatch(setInboxFilters({ skip: 0, status: searchParams.get('tab') === LIVECHAT_SCREEN_TABS.OPEN ? CONVERSATION_STATUSES.STARTED : CONVERSATION_STATUSES.FINISHED }));
      if (LIVECHAT_SCREEN_TABS.SETTINGS === previousTab) {
        setTriggerEffectFlag(triggerEffectFlag + 1);
      }
    }
  }, [searchParams.get('tab')]);

  // The following condition checks whether a next page should be requested
  useEffect(() => {
    if (conversations) {
      setShouldLoadMoreConversations(!isFetchingConversations && conversations.data.length < conversations.total);
    }
  }, [isFetchingConversations, conversations?.data.length]);

  const onItemClick = (conversation) => {
    setSearchParams({
      tab: conversation.status === CONVERSATION_STATUSES.STARTED ? LIVECHAT_SCREEN_TABS.OPEN : LIVECHAT_SCREEN_TABS.CLOSED,
      conversation: conversation.id
    });
    setInboxOpen(false);
    setIsEditingAutomations(false);
    setIsEditingResponses(false);
    if (isMobileSize) {
      setSidebarOpen(false);
    }
  };

  const onImportantClick = async (conversationId, isImportant) => {
    await dispatch(markAsImportant(conversationId, isImportant ? 'remove' : 'add', LIVECHAT_FLAGS.IMPORTANT));
  };

  return (
    <Fragment>
      <LiveChatFilters/>

      <Divider
        className="my-0"
      />

      <div
        ref={listRef}
        className="overflow-y-auto h-100"
      >
        <InfiniteScrollList
          dataSource={conversations ? conversations.data : []}
          total={conversations ? conversations.total : 0}
          renderItem={(conversation) => (
            <InboxItem
              key={conversation.id + conversation.livechatParticipants?.find(participant => participant.platformUserId === currentUser.id)?.flags.includes(LIVECHAT_FLAGS.IMPORTANT) || false}
              conversationId={conversation.id}
              sessionId={conversation.platformSessionId}
              tenantId={conversation.tenantId}
              tenantName={conversation.tenantName}
              tenantImageUrl={conversation.tenantImageUrl}
              endUserName={conversation.platformUserName}
              lastMessage={conversation.lastMessage}
              startDate={conversation.startDate}
              status={conversation.status}
              platform={conversation.platformType}
              transferRequests={conversation.livechatRequests}
              livechatParticipants={conversation.livechatParticipants}
              isImportant={conversation.livechatParticipants?.find(participant => participant.platformUserId === currentUser.id)?.flags.includes(LIVECHAT_FLAGS.IMPORTANT) || false}
              onClick={() => onItemClick(conversation)}
              onImportantClick={(important) => onImportantClick(conversation.id, important)}
            />
          )}
          parentElement={listRef.current}
          loading={inboxFilters.skip === 0 && isFetchingConversations}
          loadMore={shouldLoadMoreConversations}
          onLoadMore={(value) => {
            dispatch(setInboxFilters({ ...inboxFilters, replace: false, skip: value }));
          }}
          locale={{
            emptyText: <Row className={'mt-2'}>
              <Col>
                <Typography.Text className={'text-muted mr-1'}>
                  {searchParams.get('tab') === LIVECHAT_SCREEN_TABS.CLOSED ? t('There aren\'t any closed Conversations now.') : t('There aren\'t any open Conversations now.')}
                </Typography.Text>
              </Col>
              <Col>
                <Typography.Text className={'text-muted'}>{t('Check back later!')}</Typography.Text>
              </Col>
            </Row>
          }}
        />
      </div>
    </Fragment>
  );
};

const RequestsList = () => {
  const { orgId } = useParams();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const listRef = useRef();
  const [nextPage, setNextPage] = useState({
    skip: 0,
    take: 20,
    status: searchParams.get('tab') === LIVECHAT_SCREEN_TABS.REQUESTS_HISTORY ? [REQUEST_STATUSES.ACCEPTED, REQUEST_STATUSES.MISSED].join(',') : REQUEST_STATUSES.NEW
  });

  const { isConnected } = useSelector(state => state.livechat);
  const { selectedRequest, setSelectedRequest } = useContext(LiveChatContext);
  const { currentData: requests, isFetching: isFetchingRequests } = useFetchRequestsQuery({ orgId,
    urlParams: {
      skip: nextPage.skip,
      take: nextPage.take,
      status: nextPage.status
    }
  }, { skip: !isConnected });
  const [shouldLoadMoreRequests, setShouldLoadMoreRequests] = useState(false);

  useEffect(() => {
    if (requests && requests.data?.length && !selectedRequest) {
      setSelectedRequest(requests.data[0]);
    }
  }, [requests?.total]);

  // The following condition checks whether there are more requests left to fetch
  useEffect(() => {
    if (requests && requests.data) {
      setShouldLoadMoreRequests(!isFetchingRequests && requests.data.length < requests.total);
    }
  }, [isFetchingRequests, requests?.data?.length]);

  const getNextPage = (skip = 0, take = 20) => {
    setNextPage(prev => {
      return {
        ...prev,
        skip,
        take
      };
    });
  };

  return (
    <div
      ref={listRef}
      className="overflow-y-auto h-100 bg-body"
    >
      <InfiniteScrollList
        dataSource={requests ? requests.data : []}
        total={requests ? requests.total : 0}
        renderItem={(request) => (
          <IncomingRequest
            key={request.id}
            status={request.status}
            requestId={request.id}
            tenantId={request.tenantId}
            tenantName={request.tenantName}
            tenantImageUrl={request.tenantImageUrl}
            requestedDate={request.requestedDate}
            userId={request.platformUserId}
            userName={request.platformUserName}
            type={request.type}
            acceptedBy={request.acceptedBy}
            request={request}
          />
        )}
        parentElement={listRef.current}
        loading={!requests || isFetchingRequests}
        loadMore={shouldLoadMoreRequests}
        onLoadMore={getNextPage}
        locale={{
          emptyText: <Row>
            <Col>
              <Typography.Text className={'text-muted'}>{t('There aren\'t any new Requests now.')}</Typography.Text>
            </Col>
            <Col>
              <Typography.Text className={'text-muted'}>{t('Check back later!')}</Typography.Text>
            </Col>
          </Row>
        }}
      />
    </div>
  );
};

const LiveChatSidebarLeft = () => {
  const [searchParams] = useSearchParams();
  const isRequestsTab = searchParams.get('tab') === LIVECHAT_SCREEN_TABS.NEW || searchParams.get('tab') === LIVECHAT_SCREEN_TABS.REQUESTS_HISTORY;

  // use tab as key in order to trigger re-render when switching between new/history requests
  return (
    <Row
      wrap={false}
      className="flex-column h-100"
    >
      {isRequestsTab ? <RequestsList key={searchParams.get('tab')}/> : <ConversationsList />}
    </Row>
  );
};

export default memo(LiveChatSidebarLeft);
